import { useContext, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { getCompanyContactHistory } from '../../../../domain/use_cases/contacts/getCompanyContactHistory';
import {
  ContactExchange,
  ContactExchangesFilter,
  buildContactExchanges,
  filterContactExchanges,
  groupContactExchangesByDay,
} from '../types/ContactExchange';

// The history is left out of the contact list (heavy): fetched once, the first time the tab opens
const useContactExchangesController = (contactId: string) => {
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany } = useContext(CompanyContext);

  const [loaded, setLoaded] = useState<{ contactId: string; exchanges: ContactExchange[] } | null>(null);
  const [failedContactId, setFailedContactId] = useState<string | null>(null);
  const [filter, setFilter] = useState<ContactExchangesFilter>('all');

  const companyId = activeCompany?.id;
  const isLoaded = loaded?.contactId === contactId;
  const hasFailed = failedContactId === contactId;

  useEffect(() => {
    if (isLoaded || hasFailed || !companyId || !authentication) return;

    let isCancelled = false;
    getCompanyContactHistory({ company_id: companyId, contact_id: contactId }, authentication)
      .then((response) => {
        if (!isCancelled) setLoaded({ contactId, exchanges: buildContactExchanges(response.history) });
      })
      .catch((e) => {
        if (isCancelled) return;
        message.error(e instanceof Error ? e.message : 'Erreur lors du chargement des échanges');
        setFailedContactId(contactId);
      });
    return () => {
      isCancelled = true;
    };
  }, [isLoaded, hasFailed, contactId, companyId, authentication]);

  const days = useMemo(
    () => groupContactExchangesByDay(filterContactExchanges(isLoaded ? loaded.exchanges : [], filter)),
    [isLoaded, loaded, filter],
  );

  return {
    state: {
      days,
      filter,
      isLoading: !isLoaded && !hasFailed,
      hasFailed,
    },
    actions: {
      setFilter,
    },
  };
};

export default useContactExchangesController;
